import { useEffect, useRef } from 'react';
import * as echarts from 'echarts';
import type { EChartsOption } from 'echarts';

export interface EChartProps {
  /** The ECharts option object describing the chart. */
  option: EChartsOption;
  /** Container height (px number or any CSS length). Default 400. */
  height?: number | string;
  className?: string;
  /**
   * Renderer. Defaults to 'svg' so the Limitless CSS variables used in chart options
   * (e.g. `var(--body-color)`) resolve from the surrounding theme — canvas can't read them.
   */
  renderer?: 'svg' | 'canvas';
  /** Optional registered ECharts theme name or theme object. */
  theme?: string | object;
}

/**
 * A thin React wrapper around Apache ECharts: it owns the chart instance lifecycle
 * (init / setOption / dispose) and auto-resizes with its container via ResizeObserver.
 * Pass any ECharts `option` object; chart options ported from the Limitless template
 * live in the "Data Visualization" Storybook section.
 */
export function EChart({ option, height = 400, className, renderer = 'svg', theme }: EChartProps) {
  const elRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<echarts.ECharts | null>(null);

  // Init once per (renderer, theme); resize with the container; dispose on unmount.
  useEffect(() => {
    const el = elRef.current;
    if (!el) return;
    const chart = echarts.init(el, theme, { renderer });
    chartRef.current = chart;
    const ro = new ResizeObserver(() => chart.resize());
    ro.observe(el);
    return () => {
      ro.disconnect();
      chart.dispose();
      chartRef.current = null;
    };
  }, [renderer, theme]);

  // Apply (and replace) the option whenever it changes.
  useEffect(() => {
    chartRef.current?.setOption(option, true);
  }, [option]);

  return <div ref={elRef} className={className} style={{ width: '100%', height }} />;
}
